"use client"

import { useState } from "react"
import { API_BASE_URL } from "../../config"

const ModifiersList = ({ modifiers, onRefresh, onEdit, onAdd }) => {
  const [loading, setLoading] = useState(false)

  const handleDelete = async (modifierId) => {
    if (!window.confirm("Are you sure you want to delete this modifier list?")) {
      return
    }

    try {
      setLoading(true)
      const response = await fetch(`${API_BASE_URL}/api/admin/modifiers/${modifierId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      })

      if (response.ok) {
        onRefresh()
      } else {
        alert("Failed to delete modifier")
      }
    } catch (error) {
      console.error("Error deleting modifier:", error)
      alert("Error deleting modifier")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Modifier Lists</h2>
          <p className="text-gray-600 mt-1">Manage your product modifiers and options</p>
        </div>
        <button
          onClick={onAdd}
          className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-md font-medium"
        >
          Create New Modifier List
        </button>
      </div>

      {/* Modifiers Grid */}
      {modifiers.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-xl text-gray-600 mb-4">No modifier lists found</div>
          <button
            onClick={onAdd}
            className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-md font-medium"
          >
            Create Your First Modifier List
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modifiers.map((modifier) => {
            const modifierData = modifier.modifier_list_data || {}
            const name = modifierData.name || "Unnamed Modifier"
            const selectionType = modifierData.selection_type || "MULTIPLE"
            const modifierOptions = modifierData.modifiers || []

            return (
              <div
                key={modifier.id}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow flex flex-col h-full"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">{name}</h3>
                    <span
                      className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                        selectionType === "SINGLE" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"
                      }`}
                    >
                      {selectionType === "SINGLE" ? "Single Select" : "Multiple Select"}
                    </span>
                  </div>
                </div>

                <div className="mb-4 flex-grow">
                  <p className="text-sm text-gray-600 mb-2">
                    {modifierOptions.length} option{modifierOptions.length !== 1 ? "s" : ""}
                  </p>
                  <div className="space-y-1">
                    {modifierOptions.slice(0, 3).map((option, index) => {
                      const optionData = option.modifier_data || {}
                      const optionName = optionData.name || "Unnamed Option"
                      const price = optionData.price_money?.amount || 0

                      return (
                        <div key={index} className="flex justify-between text-sm">
                          <span className="text-gray-700 truncate mr-2">{optionName}</span>
                          <span className="text-gray-500 flex-shrink-0">
                            {price > 0 ? `+$${(price / 100).toFixed(2)}` : "Free"}
                          </span>
                        </div>
                      )
                    })}
                    {modifierOptions.length > 3 && (
                      <div className="text-sm text-gray-500">
                        +{modifierOptions.length - 3} more option{modifierOptions.length - 3 !== 1 ? "s" : ""}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons - always at bottom */}
                <div className="flex space-x-2 mt-auto">
                  <button
                    onClick={() => onEdit(modifier)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-3 rounded text-sm font-medium transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(modifier.id)}
                    disabled={loading}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-3 rounded text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ModifiersList
